<template>
  <div>
    <radioGroup
      :label="label"
      :value="internalValue"
      :class="isHorizontal() && 'horizontalAlignment'"
      :rules="validationRules"
      @change="setConfigValue"
      ref="input"
    >
      <radio
        v-for="option in options"
        :disabled="disabled"
        :key="option.Value"
        :label="option.Display"
        :value="option.Value.toString()"
        :deselectable="deselectable"
      >
        <template
          v-if="
            !option.hideTooltip && (!!option.tooltip || !!option.helpContent)
          "
          #append-label
        >
          <tooltip v-if="option.tooltip" :text="option.tooltip" />
          <helpIcon v-if="option.helpContent" :content="option.helpContent" />
        </template>
      </radio>
      <template
        v-if="!hideTooltip && (!!tooltip || !!helpContent)"
        #append-label
      >
        <tooltip v-if="tooltip" :text="tooltip" />
        <helpIcon v-if="helpContent" :content="helpContent" />
      </template>
    </radioGroup>
  </div>
</template>

<script>
/**
 * @fileoverview Config input field for radio inputs
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import radio from "@/qux-common/qux22/components/molecules/radio";
import radioGroup from "@/qux-common/qux22/components/molecules/radio-group";
import helpIcon from "@/components/help-icon";
import tooltip from "@/components/tooltip";
import fieldMap from "./field-map.json";
import getValidationRules from "@/util/getValidationRules";

export default {
  name: "RadioField",
  components: { radio, radioGroup, helpIcon, tooltip },
  data() {
    return {
      internalValue: this.value !== undefined && this.value.toString(),
    };
  },
  props: {
    category: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    id: {
      type: String,
    },
    label: {
      type: String,
    },
    options: {
      type: Array,
    },
    rules: {
      type: Array,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: [String, Boolean],
    },
    parentField: {
      type: String,
    },
    helpContent: {
      type: String,
    },
    deselectable: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    /**
     * Sets the value
     */
    setConfigValue(value) {
      // This is somewhat hacky, changing the value to null actually triggers a second onChange event, so needing to work around that.
      // We need this to be able to support unselecting a selected radio in our bulk edit screen
      if (this.internalValue === null) {
        this.internalValue = undefined;
      } else {
        value === null
          ? this.$emit("unsetConfigValue")
          : this.$emit("setConfigValue", value);
        this.internalValue = value;
      }
    },
    getFieldValue(value) {
      return (
        fieldMap[this.category] &&
        fieldMap[this.category][this.id] &&
        fieldMap[this.category][this.id][value]
      );
    },
    isHorizontal() {
      // if its a Yes No radio, lets automatically make it horizontal
      if (
        this.options != undefined &&
        this.options.length == 2 &&
        this.options.some((option) => option.Display === "Yes") &&
        this.options.some((option) => option.Display === "No")
      ) {
        return true;
      } else {
        return false;
      }
    },
    validate() {
      return this.$refs.input.validate();
    },
  },
  computed: {
    validationRules() {
      return getValidationRules({
        rules: this.rules,
        inputErrorMessage: this.getFieldValue("inputErrorMessage"),
      });
    },
  },
  watch: {
    value(value) {
      if (value) this.internalValue = value.toString();
    },
  },
};
</script>

<style lang="scss" scoped>
.v-input {
  &::v-deep {
    .radio-fields {
      display: flex;
      flex-direction: column;
      align-items: baseline;
      gap: $qvo-spacing-16;

      .v-radio {
        margin-bottom: 0;
        .v-label {
          display: flex;
          align-items: center;
          gap: $qvo-spacing-8;
        }
      }
    }

    .v-radio {
      margin-right: 1em;
    }
  }
}

.horizontalAlignment {
  &::v-deep {
    .radio-fields {
      flex-direction: row;
      gap: 0;
    }
  }
}
</style>